import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { debounce } from 'lodash'
import styled from 'styled-components'
import Throbber from '../DesignerCommon/Throbber'
import { FEED_RESET, LOAD_PENDING } from '../../redux/constants'
import FeedLoadingCard from './FeedLoadingCard'
import { BundleBanners } from './BundleBanners'
import { getUserToken } from '../../utils/fetchUtils'
import PurchaseProvider from '../MembershipModal/components/PurchaseProvider'
import { APPID } from '../../constants/ApiEndpoints'
import { useIsMobile } from '../../hooks/useIsMobile'
import membershipBannerMobile from '../../assets/itemsFeed/membershipBannerMobile.png'
import { setShowMembershipModal } from '../../redux/designerReducers/designerReducer'
import { useDispatch } from 'react-redux'
import { fetchPurchaseKeys } from '../../redux/designerReducers/cartReducer'
import { fetchPurchaseEffects } from '../../redux/designerReducers/effectsReducer'
import { fetchFeed } from '../../redux/feedInfiniteSlice'

const NoItemsFoundContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: #c4c4c4;
  padding-left: 20px;
  padding-right: 20px;
`

const NoItemsFound = styled.div`
  width: 100%;
  height: 250px;
  margin-top: 100px;
  background-image: url('../assets/noitems.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`

export const MembershipAdBanner = styled.img`
  width: 97%;
`

const LoadingContainer = styled.div`
  margin: 0 auto;
`

const RefreshTextAligner = styled.div`
  text-align: center;
`

const RefreshText = styled.p`
  color: #979797;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 23px;
`

const RefreshButton = styled.button`
  color: #ff5e6d;
  font-size: 14px;
  font-weight: bold;
  line-height: normal;
  text-decoration-line: underline;
  border: none;
  background: transparent;
  cursor: pointer;
`

const Feed = ({
  data,
  tick,
  onFetch,
  onClick,
  onDelete,
  onContextMenu,
  card,
  loading,
  error,
  from,
  limit,
  limitLock,
  end,
  showBundlesBanner,
  showMembershipAds,
  noItemsText = 'NO ITEMS YET',
  showPurchasedText,
  ...props
}) => {
  const isMobile = useIsMobile()
  const [showCardLoading, setShowCardLoading] = useState(false)
  const ob = useRef()
  const dispatch = useDispatch()

  useEffect(() => {
    if (end) setShowCardLoading(false)
    else setShowCardLoading(true)
  }, [end])

  const lastFeedItem = useCallback(
    debounce(node => {
      if (ob.current) ob.current.disconnect()
      ob.current = new IntersectionObserver(
        e => {
          if (e[0].isIntersecting) {
            if (onFetch) {
              onFetch()
            }
          }
        },
        {
          threshold: 0.01
        }
      )
      if (node) ob.current.observe(node)
    }, 500),
    [onFetch]
  )

  const dataSource = useMemo(() => {
    const limitFinal =
      limit && data.length > limit && limitLock
        ? limit + 1
        : limit || data.length

    const idsMap = {}

    return data
      .filter(item => {
        const objectId = item?.objectId || item?.item?.objectId

        if (idsMap[objectId]) return false
        else idsMap[objectId] = true

        return true
      })
      .slice(0, limitFinal)
  }, [data, limitLock])

  if (loading === LOAD_PENDING && (!data || data.length === 0)) {
    return (
      <LoadingContainer>
        <Throbber />
      </LoadingContainer>
    )
  }

  const bundlesBanner = showBundlesBanner ? (
    <PurchaseProvider
      scheme={{}}
      appid={parseInt(APPID)}
      token={getUserToken()}
    >
      <BundleBanners />
    </PurchaseProvider>
  ) : null

  if (!data) return <></>
  if (data && data.length === 0 && error === 'empty')
    return (
      <>
        {bundlesBanner}
        {showPurchasedText ? (
          <RefreshTextAligner>
            <RefreshText>
              Unleash your creativity with special bundles. Elevate your designs
              today!
            </RefreshText>
            <RefreshButton
              onClick={async () => {
                await dispatch(fetchPurchaseKeys())
                await dispatch(fetchPurchaseEffects())
                await dispatch(fetchFeed('purchased')({ status: FEED_RESET }))
              }}
            >
              Refresh
            </RefreshButton>
          </RefreshTextAligner>
        ) : (
          <NoItemsFoundContainer>
            <NoItemsFound />
            <div>{noItemsText}</div>
          </NoItemsFoundContainer>
        )}
      </>
    )

  const beforeBanner = dataSource.slice(0, showMembershipAds ? 5 : 6)
  const afterBanner = dataSource.slice(showMembershipAds ? 5 : 6)

  const renderCards = source => {
    return source.map((item, index) => {
      const Card = card
      return (
        <Card
          key={index}
          index={index}
          data={item}
          tick={tick}
          from={from}
          locked={limitLock && index === limit ? true : false}
          limitInfo={data.length - limit}
          refFn={lastFeedItem}
          onClick={onClick}
          onDelete={onDelete}
          onContextMenu={onContextMenu}
          {...props}
        />
      )
    })
  }

  if (!showBundlesBanner && !showMembershipAds)
    return <>{renderCards(dataSource)}</>

  return (
    <>
      {!isMobile && bundlesBanner}
      {renderCards(beforeBanner)}
      {isMobile && bundlesBanner}
      {isMobile && showMembershipAds && (
        <MembershipAdBanner
          onClick={() => {
            dispatch(setShowMembershipModal(true))
          }}
          src={membershipBannerMobile}
          alt=""
        />
      )}
      {renderCards(afterBanner)}
      {!limit && showCardLoading && <FeedLoadingCard />}
    </>
  )
}

export default Feed
